/**
 * v1 Organizations API Client
 * Direct integration with organizations collection
 */

import { apiClient } from '@/lib/api-client'
import { API_CONFIG } from '@/lib/config'

const BASE_URL = API_CONFIG.BASE

/**
 * Organization interface (v1 format)
 */
export interface Organization {
  _id: string
  name: string
  displayName: string
  domain?: string
  isActive: boolean
  isDeleted: boolean
  deletedAt?: string
  createdAt: string
  updatedAt: string
  userCount?: number
  tenantId?: string
  tenantName?: string
}

/**
 * Organization list response
 */
export interface OrganizationListResponse {
  organizations: Organization[]
  total: number
  page: number
  limit: number
  totalPages: number
  has_more: boolean
  has_previous: boolean
}

/**
 * Create organization request payload
 */
export interface CreateOrganizationRequest {
  name: string
  displayName: string
  domain?: string
  isActive?: boolean
  tenantId?: string
}

/**
 * Update organization request payload
 */
export interface UpdateOrganizationRequest {
  displayName?: string
  domain?: string
  isActive?: boolean
}

/**
 * Add user to organization request payload
 */
export interface AddUserToOrgRequest {
  email: string
}

/**
 * Transfer summary
 */
export interface TransferSummary {
  agentsTransferred: number
  promptsTransferred: number
  aclEntriesUpdated: number
}

/**
 * Add user to organization response
 */
export interface AddUserToOrgResponse {
  user_id: string
  email: string
  action: 'created' | 'transferred' | 'already_member'
  organization_id: string
  organization_name?: string
  transfer_summary?: TransferSummary
}

/**
 * Remove user from organization response
 */
export interface RemoveUserFromOrgResponse {
  user_id: string
  email: string
  organization_id: string
  organization_name: string
  transfer_summary?: TransferSummary
}

/**
 * Organization users response
 */
export interface OrganizationUsersResponse {
  users: User[]
  total: number
  page: number
  limit: number
  totalPages: number
  has_more: boolean
  has_previous: boolean
}

/**
 * User interface (for organization users)
 */
export interface User {
  _id: string
  email: string
  name?: string
  username?: string
  role: string
  orgId?: string
  createdAt?: string
}

/**
 * Users by domain response
 */
export interface UsersByDomainResponse {
  users: User[]
  count: number
  domain: string
}

/**
 * Bulk transfer request
 */
export interface BulkTransferRequest {
  user_ids: string[]
}

/**
 * Bulk transfer response
 */
export interface BulkTransferResponse {
  successful: Array<{
    user_id: string
    email: string
    transfer_summary: TransferSummary
  }>
  failed: Array<{
    user_id: string
    email?: string
    error: string
  }>
  summary: {
    total: number
    succeeded: number
    failed: number
    totalAgentsTransferred: number
    totalPromptsTransferred: number
    totalAclEntriesUpdated: number
  }
}

/**
 * v1 Organizations API
 */
export const v1OrganizationsApi = {
  /**
   * Get list of all organizations with pagination
   */
  getOrganizations: async (
    page = 1, 
    limit = 10, 
    search?: string, 
    sortBy?: string, 
    sortOrder?: string
  ): Promise<OrganizationListResponse> => {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit)
    })

    if (search) {
      params.append('search', search)
    }

    if (sortBy) {
      params.append('sort_by', sortBy)
    }

    if (sortOrder) {
      params.append('sort_order', sortOrder)
    }

    const response = await apiClient.get(`${BASE_URL}/organizations?${params}`)
    return response.data.data
  },

  /**
   * Get detailed information for a specific organization
   */
  getOrganizationById: async (id: string): Promise<Organization> => {
    const response = await apiClient.get(`${BASE_URL}/organizations/${id}`)
    return response.data.data
  },

  /**
   * Create a new organization
   */
  createOrganization: async (data: CreateOrganizationRequest): Promise<Organization> => {
    const response = await apiClient.post(`${BASE_URL}/organizations`, data)
    return response.data.data
  },

  /**
   * Update an organization
   */
  updateOrganization: async (id: string, data: UpdateOrganizationRequest): Promise<Organization> => {
    const response = await apiClient.put(`${BASE_URL}/organizations/${id}`, data)
    return response.data.data
  },

  /**
   * Soft delete an organization
   */
  deleteOrganization: async (id: string): Promise<Organization> => {
    const response = await apiClient.delete(`${BASE_URL}/organizations/${id}`)
    return response.data.data
  },

  /**
   * Get users belonging to an organization
   */
  getOrganizationUsers: async (
    orgId: string, 
    page = 1, 
    limit = 10, 
    search?: string
  ): Promise<OrganizationUsersResponse> => {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit)
    })

    if (search) {
      params.append('search', search)
    }

    const response = await apiClient.get(`${BASE_URL}/organizations/${orgId}/users?${params}`)
    return response.data.data
  },

  /**
   * Add user to organization by email
   */
  addUserToOrganization: async (
    orgId: string, 
    data: AddUserToOrgRequest
  ): Promise<AddUserToOrgResponse> => {
    const response = await apiClient.post(`${BASE_URL}/organizations/${orgId}/users`, data)
    return response.data.data
  },

  /**
   * Remove user from organization
   */
  removeUserFromOrganization: async (
    orgId: string, 
    userId: string
  ): Promise<RemoveUserFromOrgResponse> => {
    const response = await apiClient.delete(`${BASE_URL}/organizations/${orgId}/users/${userId}`)
    return response.data.data
  },

  /**
   * Get users by email domain for bulk migration
   */
  getUsersByDomain: async (
    orgId: string,
    domain: string,
    excludeOrgId?: string,
    limit = 100
  ): Promise<UsersByDomainResponse> => {
    const params = new URLSearchParams({
      domain,
      limit: String(limit)
    })
    if (excludeOrgId) {
      params.append('exclude_org_id', excludeOrgId)
    }
    const response = await apiClient.get(`${BASE_URL}/organizations/${orgId}/users/by-domain?${params}`)
    return response.data.data
  },

  /**
   * Bulk transfer multiple users to organization
   */
  bulkTransferUsers: async (
    orgId: string,
    data: BulkTransferRequest
  ): Promise<BulkTransferResponse> => {
    const response = await apiClient.post(`${BASE_URL}/organizations/${orgId}/users/bulk-transfer`, data)
    return response.data.data
  }
}
